import { faXmark, faPencil, faCirclePlus } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useState } from 'react'
import { flushSync } from 'react-dom'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import TwinIcon from '../../../../TwinIcon'
import TwinTagSelector from '../../../../TwinTagSelector'
import TwinTrans from '../../../../TwinTrans'
import { SetCompleteDataTy } from '../../types'
import { InputTableLeftColumnChange } from '../types'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import ModalCESellTag from '../../../../../app/erp/screens/Logistic/QuoteParent/SellParent/SellTags/ModalCESellTag'
import { OnSubmit } from '../../../../../forms/TwinForm/types'
import { getUserPermissions } from '../../../../../utils/reducers/getters'
import ModalCEBuyTag from '../../../../../app/erp/screens/Logistic/BuyParent/BuyTags/ModalCEBuyTag'
import { CustomSelectSearchBarWithCheckBoxColorStateLess } from '../../../../../forms/CustomSelect/CustomSelectTags'
import PermissionChecker from '../../../../PermissionChecker'

interface ITHeaderOrderTagsProps extends ITHeaderOrderTagsLogicProps {
    className?: string
    readOnly?: boolean
    dictTags: TwinDictionary
    selectedTags: string[]
}

export const ITHeaderOrderTags: React.FC<ITHeaderOrderTagsProps> = ({ className, selectedTags, dictTags, readOnly = false, ...logic }) => {
    const { addTag, removeTag, editOrderTags, setEditOrderTags } = useITHeaderOrderTagsLogic({...logic})
    return (
        <div className={(className || '')}>
            <div className='flex items-center'>
                <span className='mr-10'>
                    <TwinTrans transKey='tags'>Etiquetas</TwinTrans>
                </span>
                {!readOnly ? <TwinIcon className='cursor-pointer' icon={editOrderTags ? faXmark : faPencil} onClick={() => setEditOrderTags((old) => !old)} /> : null }
            </div>
            <TwinTagSelector dictTags={dictTags} readOnly={!editOrderTags} selectedTags={selectedTags || []} {...{ addTag, removeTag }} />
        </div>
    )
}

interface ITHeaderOrderTagsLogicProps {
    setCompleteData: SetCompleteDataTy
    headerData: TwinDictionary
    onChange: InputTableLeftColumnChange
}

const useITHeaderOrderTagsLogic = ({ headerData, setCompleteData, onChange }: ITHeaderOrderTagsLogicProps) => {
    const [editOrderTags, setEditOrderTags] = useState<boolean>(false)

    const saveTagsChanges = useCallback(async (tags: string[]) => {
        onChange(headerData?.id, 'tagsForParse', tags?.join(';') || '')
    }, [headerData?.id, onChange])

    const addTag = useCallback(async (TagId: number) => {
        await flushSync(async () => {
            let tags: string[] = []
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                if (!copyOld?.header?.tags) {
                    copyOld.header.tags = []
                }
                copyOld.header.tags.push(parseInt(String(TagId)))
                tags = copyOld.header.tags
                return { ...copyOld }
            })
            await saveTagsChanges(tags)

        })
    }, [setCompleteData, saveTagsChanges])

    const removeTag = useCallback(async (posArr: number) => {
        let tags: string[] = []
        await flushSync(async () => {
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.header.tags.splice(posArr, 1)
                tags = copyOld.header.tags
                return { ...copyOld }
            })
            await saveTagsChanges(tags)
        })
    }, [setCompleteData, saveTagsChanges])

    return { editOrderTags, setEditOrderTags, addTag, removeTag, saveTagsChanges }
}

interface ITHeaderOrderTagsNewProps extends ITHeaderOrderTagsNewLogicProps {
    className?: string
    dictTags: TwinDictionary
    readOnly?: boolean
    tagType?: number
}

export const ITHeaderOrderTagsNew: React.FC<ITHeaderOrderTagsNewProps> = ({ className, selectedTags, dictTags, getCompleteData, readOnly, tagType, ...logic }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal, myOnSubmit, myOnChange, handleOnRemove, markAllAsSelected, mySelectedValues } = useITHeaderOrderTagsNewLogic({ selectedTags, getCompleteData, ...logic })
    const sellTagsPermission = getUserPermissions('sell.tags') || { n: 1, permission: 'read' }
    return (
        <CustomSelectSearchBarWithCheckBoxColorStateLess label={t('tags', 'Etiquetas')} items={dictTags} className={className} displayTagsLength={2} onChange={myOnChange} readOnly={readOnly} selectedValues={mySelectedValues} handleOnRemove={handleOnRemove} extraDropDownParams={{ markAllAsSelected, selectedValues: mySelectedValues, onChange: myOnChange }}>
            <PermissionChecker userPermissions={sellTagsPermission} permission={'create'}>
                {!readOnly ? <div className='flex items-center mt-22 -ml-8 regular18 text-green-21 add_tag_icon cursor-pointer' onClick={() => setOpenModal({ type: 'create', allRowData: { type: tagType } })}><TwinIcon icon={faCirclePlus} /></div> : null}
                {openModal?.type === 'create' ? <ModalCESellTag setOpened={() => setOpenModal(null)} onSubmit={myOnSubmit} userPermissions={getUserPermissions('sell.tags') || { n: 1, permission: 'read' }} allRowData={tagType ? openModal?.allRowData : undefined} readOnly={tagType ? true : false} /> : null}
            </PermissionChecker>
        </CustomSelectSearchBarWithCheckBoxColorStateLess>
    )
}

interface ITHeaderOrderTagsNewLogicProps {
    selectedTags: string[]
    getCompleteData?: () => void
    headerData: TwinDictionary;
    onChange: InputTableLeftColumnChange;
}

const useITHeaderOrderTagsNewLogic = ({ selectedTags, getCompleteData, onChange, headerData }: ITHeaderOrderTagsNewLogicProps) => {
    const [mySelectedValues, setMySelectedValues] = useState(() => {
        const mySelectedTags = []
        for (const element of selectedTags) {
            mySelectedTags.push(String(element))
        }
        return mySelectedTags
    })
    const { openModal, setOpenModal } = useEditDeleteModal()
    
    const myOnChange = useCallback(async (value: string) => {
        flushSync(async () => {
            let myCurrentItems: string[] = []
            await setMySelectedValues((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const currentItems: any[] = [...copyOld]
                const position = currentItems.indexOf(value)
                if (position !== -1) {
                    currentItems.splice(position, 1)
                } else {
                    currentItems.push(String(value))
                }
                myCurrentItems = currentItems
                return currentItems
            })
            onChange(headerData?.id, 'tagsForParse', myCurrentItems?.join(';') || '')
        })
    }, [onChange, headerData?.id])

    const markAllAsSelected = useCallback(async (e: any, parsedFilteredItems: TwinDictionary[], isAllSelected: boolean) => {
        e.stopPropagation()
        flushSync(async () => {
            let myCurrentItems: string[] = []
            await setMySelectedValues((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const currentItems: any[] = copyOld
                for (const item of parsedFilteredItems) {
                    const value = String(item.id)
                    if (isAllSelected) {
                        const index = currentItems.indexOf(value)
                        if (index !== -1) {
                            currentItems.splice(index, 1)
                        }
                    } else {
                        if (!currentItems.includes(value)) {
                            currentItems.push(value)
                        }
                    }
                }
                myCurrentItems = currentItems
                return currentItems
            })
            onChange(headerData?.id, 'tagsForParse', myCurrentItems?.join(';') || '')  
        })
    }, [onChange, headerData?.id])

    const handleOnRemove = useCallback(async (value: string | number) => {
        flushSync(async () => {
            let myCurrentItems: string[] = []
            await setMySelectedValues((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                const currentItems: any[] = copyOld
                const position = currentItems.indexOf(value)
                if (position !== -1) {
                    currentItems.splice(position, 1)
                }
                myCurrentItems = currentItems
                return currentItems
            })
            onChange(headerData?.id, 'tagsForParse', myCurrentItems?.join(';') || '')
        })
    }, [onChange, headerData?.id])

    const myOnSubmit: OnSubmit = useCallback(async (e) => {
        const result = await e.json()
        if (result) {
            await myOnChange(String(result?.id))
            getCompleteData?.()
        }
    }, [myOnChange, getCompleteData])
    
    return { openModal, setOpenModal, myOnSubmit, myOnChange, handleOnRemove, markAllAsSelected, mySelectedValues }
}

interface ITHeaderBuyOrderTagsNewProps {
    className?: string
    dictTags: TwinDictionary
    readOnly?: boolean
    selectedTags: string[]
    getCompleteData?: () => void
    headerData: TwinDictionary;
    onChange: InputTableLeftColumnChange;
}

export const ITHeaderBuyOrderTagsNew: React.FC<ITHeaderBuyOrderTagsNewProps> = ({ className, selectedTags, dictTags, getCompleteData, readOnly, ...logic }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useITHeaderBuyOrderTagsLogic()
    const { myOnSubmit, myOnChange, handleOnRemove, markAllAsSelected, mySelectedValues } = useITHeaderOrderTagsNewLogic({ selectedTags, getCompleteData, ...logic })
    const buyTagsPermission = getUserPermissions('buy.tags') || { n: 4, permission: 'delete' }
    return (
        <CustomSelectSearchBarWithCheckBoxColorStateLess label={t('tags', 'Etiquetas')} items={dictTags} className={className} displayTagsLength={2} onChange={myOnChange} readOnly={readOnly} selectedValues={mySelectedValues} handleOnRemove={handleOnRemove} extraDropDownParams={{ markAllAsSelected, selectedValues: mySelectedValues, onChange: myOnChange }}>
            <PermissionChecker userPermissions={buyTagsPermission} permission={'create'}>
                {!readOnly ? <div className='flex items-center mt-22 -ml-8 regular18 text-green-21 add_tag_icon cursor-pointer' onClick={() => setOpenModal({ type: 'create', allRowData: {} }) }><TwinIcon icon={faCirclePlus} /></div> : null}
                {openModal?.type === 'create' ? <ModalCEBuyTag setOpened={() => setOpenModal(null)} onSubmit={myOnSubmit} userPermissions={buyTagsPermission} /> : null}
            </PermissionChecker>
        </CustomSelectSearchBarWithCheckBoxColorStateLess>
    )
}

const useITHeaderBuyOrderTagsLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return { openModal, setOpenModal }
}
